/**
 * Top-bar account controls: theme cycle, signed-in name, and sign out.
 * Theme is a single icon button that steps System → Light → Dark.
 */

import {
  faDesktop,
  faGlobe,
  faMoon,
  faRightFromBracket,
  faSun,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/lib/auth';
import { applyThemeMode, readThemeMode, type ThemeMode } from '@/lib/theme';
import { useI18n } from '@/lib/i18n';
import type { SupportedLocale } from '@/lib/locale';

const THEME_ORDER: ThemeMode[] = ['system', 'light', 'dark'];

const LANGUAGES: ReadonlyArray<{ locale: SupportedLocale; label: string }> = [
  { locale: 'en', label: 'English' },
  { locale: 'zh-CN', label: '简体中文' },
  { locale: 'vi', label: 'Tiếng Việt' },
];

const THEME_ICON = {
  system: faDesktop,
  light: faSun,
  dark: faMoon,
} as const;

function nextThemeMode(mode: ThemeMode): ThemeMode {
  const index = THEME_ORDER.indexOf(mode);
  return THEME_ORDER[(index + 1) % THEME_ORDER.length] ?? 'system';
}

const controlClassName =
  'text-shell-top-foreground hover:bg-shell-top-hover hover:text-shell-top-foreground';

export function UserMenu() {
  const { user, handoffLoginName, signOut } = useAuth();
  const { locale, selectLocale, t } = useI18n();
  const name = user?.name ?? user?.username ?? handoffLoginName ?? t('common.signedIn');
  const [themeMode, setThemeMode] = useState<ThemeMode>(readThemeMode);

  function cycleTheme(): void {
    const next = nextThemeMode(themeMode);
    setThemeMode(next);
    applyThemeMode(next);
  }

  const themeLabel = t(`theme.${themeMode}`);
  const nextLabel = t(`theme.${nextThemeMode(themeMode)}`);

  return (
    <div className="flex items-center gap-0.5">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t('language.current', {
              current: LANGUAGES.find((language) => language.locale === locale)?.label ?? locale,
            })}
            className={controlClassName}
          >
            <FontAwesomeIcon icon={faGlobe} aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t('language.label')}</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={locale}
            onValueChange={(value) => {
              const language = LANGUAGES.find((candidate) => candidate.locale === value);
              if (language !== undefined) selectLocale(language.locale);
            }}
          >
            {LANGUAGES.map((language) => (
              <DropdownMenuRadioItem key={language.locale} value={language.locale}>
                <span lang={language.locale === 'zh-CN' ? 'zh-Hans' : language.locale}>
                  {language.label}
                </span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t('theme.switch', { current: themeLabel, next: nextLabel })}
            onClick={cycleTheme}
            className={controlClassName}
          >
            <FontAwesomeIcon icon={THEME_ICON[themeMode]} aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {t('theme.switch', { current: themeLabel, next: nextLabel })}
        </TooltipContent>
      </Tooltip>

      <span
        className="flex max-w-40 items-center gap-2 truncate px-2 text-sm text-shell-top-foreground"
        title={name}
      >
        <FontAwesomeIcon icon={faUser} className="shrink-0 text-shell-top-muted" aria-hidden />
        <span className="hidden truncate sm:inline">{name}</span>
        <span className="sr-only sm:hidden">{t('common.signedIn')} {name}</span>
      </span>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t('common.signOut')}
            onClick={() => {
              void signOut();
            }}
            className={controlClassName}
          >
            <FontAwesomeIcon icon={faRightFromBracket} aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('common.signOut')}</TooltipContent>
      </Tooltip>
    </div>
  );
}
